import { useEffect, useMemo, useRef, useState } from 'react';
import { FAQPageJsonLd, NextSeo, ProductJsonLd } from 'next-seo';
import { AppRoutes, ApiRoutes, AdminRoutes } from 'src/lib/routes';
import Image from 'next/image';
import ScrollableCollectionOptions from 'src/components/ScrollableCollectionOptions';
import OtherOptions from 'src/components/OtherOptions';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import RevalidateSettings from 'src/lib/revalidate_settings';
import { Collection, CollectionPage, Pagination, SimpleCollection, Image as TImage, TravelItinerary } from 'src/types';
import Link from 'next/link';
import ApiClient from 'src/lib/api_client';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import imgGiftCards from 'public/images/gift_cards.png';
import Loading from 'src/components/Loading';
import { truncate, flatten, sortBy } from 'lodash-es';
import AdminBar from 'src/components/AdminBar';
import useFilter from 'src/hooks/useFilter';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import UserFaqSection from 'src/components/UserFaqSection';
import useCurrentUser from 'src/hooks/useCurrentUser';
import CollectionHorizontalCard from 'src/components/CollectionHorizontalCard';
import pluralize from 'pluralize';
import { DefaultCollectionHeroImage, commonCarouselSettings, HEADER_HEIGHT } from 'src/lib/constants';
import useFetchGroupOptions from 'src/hooks/useFetchFeaturedCollectionOptions';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import Slider from 'react-slick';
import ImageWithFallback from 'src/components/ImageWithFallback';
import ReadMore from 'src/components/ReadMore';
import imageStyle from 'src/styles/modules/components/image.module.scss';
import styles from 'src/styles/modules/pages/brand.module.scss';
import { BsStarFill } from 'react-icons/bs';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import Homework from 'src/components/Homework';
import BlogCardV2 from 'src/components/BlogCardV2';
import { scrollTo } from 'src/helpers';
import useQuery from 'src/hooks/useQuery';
import useInitialData from 'src/hooks/useInitialData';
import useBreakpoints from 'src/hooks/useBreakpoints';
import TopCollectionSection from 'src/components/city/TopCollectionSection';
import CountryCityPageNav from 'src/components/CountryCityPageNav';
import useFetchCountryOrCityCollections from 'src/hooks/useFetchCountryOrCityCollections';
import Banner from 'src/components/Banner';
import WhatOtherGuestsSay from 'src/components/WhatOtherGuestsSay';

const metaData = (collection: Collection, tagNames: string) => {
  const currentYear = new Date().getFullYear();
  return {
    name: `${collection.kind === 'list' ? '' : `${collection.optionsCount} Best `}${
      collection.name
    } in ${currentYear}`,
    desc: collection.tagId
      ? `Not your usual touristy stuff - Fun activities for ${collection.name} [${currentYear}]. Try ${tagNames} and more!`
      : collection.optionLocationId
      ? `Not your usual touristy stuff - the Best ${collection.name} [${currentYear}]. Try ${tagNames} and more!`
      : collection.description,
  };
};

const CollectionShow: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  reviews,
  collection,
  tagNames,
  relatedCollections: relatedCollectionData,
  faqs: faqData,
  featuredOptions,
  blogs,
  categories: initialCategories,
  categoryOptions: initialCategoryOptions,
}) => {
  const initialData = useInitialData();
  const { isScreenMd } = useBreakpoints();
  const containerOptionsRef = useRef<HTMLDivElement>(null)
  const countryOrCityRecord = collection.cityId
    ? initialData.cities.find((o) => o.id === collection.cityId)
    : collection.countryId
    ? initialData.countries.find((o) => o.id === collection.countryId)
    : undefined;

  const { data: faqs, pagination: faqPagination } = faqData;
  const [currentUser] = useCurrentUser();

  const {
    collections: categories,
    collectionOptions: categoryOptions,
    isLoading,
  } = useFetchGroupOptions({
    route: ApiRoutes.apiCollectionCategoryOptionsRoute,
    record: collection,
    initialData: {
      collections: initialCategories,
      collectionOptions: initialCategoryOptions,
    },
  });
  const sortedBlogs = sortBy(blogs, (o) =>
    o.linkableType === 'Collection' && Number(o.linkableId) === collection.id ? 0 : 1
  );
  const { data: travelItineraryData } = useQuery<{
    data: TravelItinerary[];
    pagination: Pagination;
  }>(
    ApiRoutes.apiTravelItinerariesRoute(
      {},
      {
        q: {
          g: {
            0: {
              status_eq: 'published',
            },
            1: {
              g: {
                0: {
                  country_id_eq: collection.countryId,
                },
                1: {
                  travel_itinerary_cities_city_country_id_eq: collection.countryId,
                },
                2: {
                  travel_itinerary_cities_city_id_in: collection.cityId ? [collection.cityId] : [],
                },
              },
              m: 'or',
            },
          },
          s: 'views_count desc',
        },
        page: 1,
        per_page: 20,
      }
    ).toUrl()
  );

  const { data: itineraries = [] } = travelItineraryData || {};
  const cities = useMemo(
    () =>
      sortBy(
        initialData.cities.filter((o) => o.countryId === collection.countryId),
        [(o) => (o.featured ? 0 : 1), (o) => -o.optionsCount]
      ),
    [collection.id, initialData.cities.length]
  );

  const { locationCollections, listCollections, activityCollections } =
    useFetchCountryOrCityCollections(countryOrCityRecord, collection.cityId ? 'City' : 'Country');

  const [popupType, setPopupType] = useState<'collections' | undefined>(undefined);

  const { setFilterRecord } = useFilter();
  const meta = metaData(collection, tagNames);
  const images =
    collection.images && collection.images.length > 0
      ? collection.images
      : [DefaultCollectionHeroImage];
  const image: TImage | undefined = images[0];
  const increaseViewCount = useEnhanceApiClient(ApiClient.increaseViewCount);

  useEffect(() => {
    if (collection.id) {
      setFilterRecord({
        id: collection.id,
        type: 'Collection',
        name: collection.name,
      });

      increaseViewCount({
        view_countable_id: collection.id,
        view_countable_type: 'Collection',
      });
    }
  }, [collection.id]);

  return (
    <>
      <NextSeo
        title={meta.name}
        description={meta.desc}
        canonical={AppRoutes.collectionRoute({ id: collection.slugCode }).toUrl()}
        openGraph={{
          title: meta.name,
          description: meta.desc,
          images: image?.mdUrl
            ? [
                {
                  url: image.mdUrl,
                  width: 600,
                  height: 600,
                  alt: collection.name,
                },
              ]
            : undefined,
        }}
      />
      <WithDefaultJsonLd>
        <ProductJsonLd
          productName={collection.name}
          description={collection.description}
          images={image?.mdUrl ? [image.mdUrl] : []}
          offers={{
            url: AppRoutes.collectionRoute({ id: collection.slugCode }).toUrl(),
            availability: 'https://schema.org/InStock',
          }}
          aggregateRating={{
            ratingValue: collection.reviewRating,
            reviewCount: collection.ratedReviewsCount,
          }}
        />
        {collection.nextSeoFaqs ? <FAQPageJsonLd mainEntity={collection.nextSeoFaqs} /> : null}
      </WithDefaultJsonLd>

      {countryOrCityRecord ? (
        <CountryCityPageNav
          record={countryOrCityRecord}
          blogs={sortedBlogs}
          travelItineraries={itineraries}
          listCollections={listCollections}
          activityCollections={activityCollections}
          locationCollections={locationCollections}
          cities={cities}
          collectionCategories={
            categories.map((cate) => ({
              ...cate,
              strippedDescription: cate.description,
              url: AppRoutes.cateCollectionRoute({
                id: collection.slugCode,
                cateId: cate.slug,
              }).toUrl(),
            })) as SimpleCollection[]
          }
          outerPopupType={popupType}
          onClose={() => setPopupType(undefined)}
        />
      ) : null}

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
        <AdminBar updatedAt={collection.updatedAt}>
          <>
            <a
              className="btn btn-secondary mb-2"
              href={AdminRoutes.editAdminCategorizableRoute({ id: collection.idWithClass }).toUrl()}
            >
              Manage Categories
            </a>
            <a
              className="btn btn-primary mb-2"
              href={AdminRoutes.editAdminCollectionRoute({ id: collection.id }).toUrl()}
            >
              Edit Collection
            </a>
            <a
              className="btn btn-primary mb-2"
              href={AdminRoutes.adminCollectionsRoute({}).toUrl()}
            >
              Collection Dashboard
            </a>
          </>
        </AdminBar>
        <div className="container">
          <Breadcrumb className="breadcrumb--black tw-justify-center tw-mb-4 tw-pt-0 tw-text-xs md:tw-text-sm tw-pr-0">
            {collection.cityId ? (
              <>
                {collection.citySlug !== 'singapore' ? (
                  <BreadcrumbItem>
                    <Link legacyBehavior prefetch={false}                       href={AppRoutes.countryRoute({
                        id: collection.countrySlug as string,
                      }).toUrl()}
                    >
                      <a>{collection.countryName}</a>
                    </Link>
                  </BreadcrumbItem>
                ) : null}
                <BreadcrumbItem>
                  <Link legacyBehavior prefetch={false} href={AppRoutes.cityRoute({ id: collection.citySlug as string }).toUrl()}>
                    <a>{collection.cityName}</a>
                  </Link>
                </BreadcrumbItem>
              </>
            ) : collection.countryId ? (
              <BreadcrumbItem>
                <Link legacyBehavior prefetch={false}                   href={
                    collection.countrySlug === 'singapore'
                      ? AppRoutes.cityRoute({ id: collection.countrySlug as string }).toUrl()
                      : AppRoutes.countryRoute({ id: collection.countrySlug as string }).toUrl()
                  }
                >
                  <a>{collection.countryName}</a>
                </Link>
              </BreadcrumbItem>
            ) : null}
          </Breadcrumb>
        </div>

        <div className="container tw-max-w-2xl tw-mb-8 md:tw-mb-16">
          {collection.shortDescription ? (
            <div className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-xl md:tw-text-2xl tw-mb-3 md:tw-mb-4">
              {collection.shortDescription}
            </div>
          ) : null}
          <h1 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-4xl md:tw-text-6xl">
            {collection.kind === 'location'
              ? collection.name.replace(/experiences/i, '')
              : collection.name}
          </h1>
        </div>

        <div className="container tw-max-w-8xl tw-px-2 md:tw-px-4 tw-relative">
          <Slider
            {...commonCarouselSettings}
            slidesToShow={1}
            slidesToScroll={1}
            centerMode={true}
            centerPadding="400px"
            infinite={true}
            responsive={[
              {
                breakpoint: 1040,
                settings: {
                  centerPadding: '200px',
                  arrows: false,
                },
              },
              {
                breakpoint: 800,
                settings: {
                  centerPadding: typeof window === 'object' ? `${Math.round((window.innerWidth - 296) / 2)}px` : '67px',
                  arrows: false,
                },
              },
            ]}
            className="tw--mx-2 md:tw--mx-4"
          >
            {images.map((image, index) => (
              <div key={index} className="tw-px-2 md:tw-px-4 tw-outline-none">
                <ImageWithFallback
                  record={image}
                  imgField="webpLgUrl"
                  alt=""
                  className={`tw-block tw-static ${imageStyle.heroImage}`}
                  fill
                />
              </div>
            ))}
          </Slider>
        </div>

        <div className="tw-mt-10 md:tw-mt-16 tw-text-center tw-max-w-md tw-mx-auto tw-px-1 tw-w-72 md:tw-w-full">
          <div className={styles.collectionDesc} dangerouslySetInnerHTML={{ __html: collection.description || '' }}></div>
        </div>

        {featuredOptions.length > 1 ? (
          <div className="container tw-px-4">
            <div className="tw-my-16 tw-border-t tw-border-grey-lighter"></div>
            <h2 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-3xl md:tw-text-6xl tw-mb-8 md:tw-mb-16">
              Best things to do
            </h2>

            <Slider
              {...commonCarouselSettings}
              slidesToShow={3}
              slidesToScroll={1}
              dots={false}
              arrows={true}
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1,
                    dots: true,
                    arrows: false,
                  },
                },
              ]}
              className="slider-v2 slider-collection-best-tdd tw--mx-4"
            >
              {featuredOptions.slice(0, 6).map((option) => (
                <Link legacyBehavior prefetch={false}                   key={option.id}
                  href={AppRoutes.experienceRoute({ id: option.slugCode }).toPath()}
                >
                  <a className="tw-block tw-px-4 tw-relative tw-outline-none">
                    {option.featuredTag ? (
                      <div className="tw-text-brand-navy tw-absolute tw-flex tw-flex-col tw-justify-center tw-top-3 tw-left-0 tw-rounded-full tw-bg-brand-green tw-h-24 tw-w-24 tw-p-3 tw-text-center tw-font-semibold tw-font-styleSans tw-leading-none">
                        {option.featuredTag}
                      </div>
                    ) : null}
                    <ImageWithFallback
                      record={option.images[0]}
                      imgField="webpMdUrl"
                      alt=""
                      className={`${imageStyle.cardImage} tw-block tw-static tw-object-cover tw-border-2 tw-border-solid tw-rounded-lg tw-border-brand-green tw-mb-4`}
                      fill
                    />
                    <div className="tw-text-brand-navy tw-text-center tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-2xl tw-leading-none md:tw-leading-none tw-mb-4">
                      {option.name}
                    </div>
                    <div className="tw-text-center">
                      <Link legacyBehavior prefetch={false} href={AppRoutes.experienceRoute({ id: option.slugCode }).toPath()}>
                        <a className="btn btn-primary">see more</a>
                      </Link>
                    </div>
                  </a>
                </Link>
              ))}
            </Slider>
          </div>
        ) : null}
      </div>

      {collection.optionsCount > 0 ? (
        <div className="tw-py-16 md:tw-py-24">
          <div ref={containerOptionsRef}></div>
          <div className="container tw-max-w-xs md:tw-max-w-6xl">
            <div className="tw-font-styleSans tw-font-semibold tw-text-center tw-text-3xl md:tw-text-6xl tw-leading-none md:tw-leading-none tw-mb-0">
              All {collection.name}
            </div>
          </div>
          <div className="tw-relative tw-pt-8 md:tw-pt-16">
            {categories && categories.length > 0 && categories.some(o => categoryOptions[o.id].options.length > 0) ? (
              categories.map((category, index) => (
                <div key={category.id}>
                  {categoryOptions[category.id].options.length > 0 ? (
                    <div className="container">
                      <div className="tw-mb-8 md:tw-mb-16 tw-border-t tw-border-grey-lighter"></div>
                    </div>
                  ) : null}
                  <ScrollableCollectionOptions
                    key={category.id}
                    collection={{
                      id: category.id,
                      name: category.name,
                      slug: category.slug,
                    }}
                    seeAllUrl={AppRoutes.cateCollectionRoute({
                      id: collection.slugCode,
                      cateId: category.id,
                    }).toPath()}
                    scrollContainerClassName="tw-overflow-x-auto tw--mr-3.75 md:tw-mr-0"
                    infinite={true}
                    renderTitle={() => (
                      <h2 className="tw-text-xl md:tw-text-2xl mb-0">
                        <Link legacyBehavior prefetch={false}                           href={AppRoutes.cateCollectionRoute({
                            id: collection.slugCode,
                            cateId: category.slug,
                          }).toUrl()}
                        >
                          <a className="tw-block tw-text-center md:tw-text-left tw-font-styleSans tw-text-2xl md:tw-text-4xl tw-font-semibold tw-text-brand-navy">
                            <span>{category.name}</span>
                            <HiOutlineArrowNarrowRight className="tw-ml-1 tw-inline md:tw-hidden" />
                          </a>
                        </Link>
                        {category.description && (
                          <div className="tw-text-left tw-text-sm md:tw-text-base tw-font-normal tw-text-brand-navy tw-hidden md:tw-block">
                            {category.description}
                          </div>
                        )}
                      </h2>
                    )}
                    totalCount={categoryOptions[category.id].totalCount}
                    options={categoryOptions[category.id].options}
                    isGiftExperience={!!collection.giftCountryId}
                  />
                </div>
              ))
            ) : (
              <div className="tw-mb-10 md:tw-mb-14"></div>
            )}

            {isLoading ? <Loading className="tw-justify-center tw-py-20" /> : null}

            <div className="container tw--mt-8">
              <div className="tw-mb-16 tw-border-t tw-border-grey-lighter"></div>
              <h2 className="tw-font-semibold tw-font-styleSans tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-mb-8 tw-text-center md:tw-text-left">
                {`Browse Collection`}
              </h2>
            </div>

            <OtherOptions
              hideTitle={true}
              record={{
                id: collection.id,
                type: 'Collection',
              }}
              isGiftExperience={!!collection.giftCountryId}
            />
          </div>
        </div>
      ) : null}

      {collection.giftCountryId ? (
        <div className="tw-bg-purple tw-py-16 md:tw-py-24">
          <div className="container">
            <h2 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-2xl md:tw-text-4xl">
              Can’t Decide? Get a Gift Card Instead!
            </h2>
            <div className="tw-text-center tw-mb-8">
              <Image
                src={imgGiftCards}
                fill
                alt=""
                className="tw-inline-block tw-static tw-w-full tw-h-auto md:tw-w-auto md:tw-h-64 tw--mt-8"
              />
            </div>
            <h3 className="tw-text-center tw-text-lg md:tw-text-2xl tw-leading-none tw-font-semibold tw-mb-4">
              Gift Memories Instead of More Stuff
            </h3>
            <div className="tw-w-96 tw-mx-auto tw-mb-8">
              <div className="tw-mb-2">
                ✔️{' '}
                <span className="tw-text-brand-navy tw-ml-1">
                  Gift thoughtful experiences they will cherish
                </span>
              </div>
              <div className="tw-mb-2">
                ✔️{' '}
                <span className="tw-text-brand-navy tw-ml-1">
                  Can be Used on {'>'}2500 Experiences
                </span>
              </div>
              <div className="tw-mb-2">
                ✔️{' '}
                <span className="tw-text-brand-navy tw-ml-1">
                  Personalise it for your occasion!
                </span>
              </div>
            </div>

            <div className="tw-text-center">
              <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toUrl()}>
                <a className="btn btn-primary">Buy Gift Card</a>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        ''
      )}

      <WhatOtherGuestsSay
        reviews={reviews}
        ratedReviewsCount={collection.ratedReviewsCount}
        reviewRating={collection.reviewRating}
      />

      {faqs.length > 0 ? (
        <div className="tw-bg-grey-lightest tw-py-16 md:tw-py-24">
          <div className="container md:tw-max-w-3xl">
            <UserFaqSection
              key={collection.id}
              faqs={faqs}
              title={
                collection.kind === 'location'
                  ? `${collection.name.replace(/experiences/i, '')} FAQs`
                  : `${collection.name} FAQs`
              }
              limit={5}
              hiddenAskQuestion={true}
              faqPagination={faqPagination}
              faqable={{ id: String(collection.id), type: 'Collection' }}
              isAdmin={currentUser?.isAdmin || false}
            />
          </div>
        </div>
      ) : null}

      <Homework />

      {countryOrCityRecord ? (
        <>
          <Banner
            title={`Top things to do in ${countryOrCityRecord.name}`}
            image={countryOrCityRecord.image}
            url={
              collection.cityId
                ? AppRoutes.cityRoute({ id: countryOrCityRecord.slug }).toUrl()
                : AppRoutes.countryRoute({ id: countryOrCityRecord.slug }).toUrl()
            }
            urlLabel={`explore ${countryOrCityRecord.name}`}
          />

          {!collection.giftCountryId &&
          [activityCollections, locationCollections, listCollections, cities].some(
            (o) => o.length > 0
          ) ? (
            <TopCollectionSection
              title={`${countryOrCityRecord.name} Collections`}
              record={{
                type: collection.countryId ? 'Country' : 'City',
                name: countryOrCityRecord.name,
                slug: countryOrCityRecord.slug,
              }}
              listCollections={listCollections}
              activityCollections={activityCollections}
              locationCollections={locationCollections}
              cities={cities}
              limit={isScreenMd ? 15 : undefined}
              onShowMore={(tab) =>
                setPopupType('collections')
              }
            />
          ) : null}
        </>
      ) : null}

      {sortedBlogs.length > 0 ? (
        <div className="tw-bg-white tw-py-16 md:tw-py-24">
          <div className="container tw-px-4">
            <div className="tw-py-4 tw-border-t tw-border-b tw-border-grey-lighter tw-mb-8">
              <h2 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-2xl md:tw-text-4xl tw-mb-0">
                Field Notes
              </h2>
            </div>
            <Slider
              {...commonCarouselSettings}
              slidesToShow={3}
              slidesToScroll={1}
              dots={false}
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1.3,
                    arrows: false,
                  },
                },
              ]}
              prevArrow={
                <div className="slick-prev">
                  <HiOutlineArrowNarrowLeft
                    size={24}
                    strokeWidth={2}
                    className="tw-text-brand-navy"
                  />
                </div>
              }
              nextArrow={
                <div className="slick-next">
                  <HiOutlineArrowNarrowRight
                    size={24}
                    strokeWidth={2}
                    className="tw-text-brand-navy"
                  />
                </div>
              }
              className="tw--mx-2 md:tw--mx-4 slider-v2 carousel-field-notes"
            >
              {sortedBlogs.slice(0, 9).map((blog) => (
                <div key={blog.id} className="tw-px-2 md:tw-px-4">
                  <BlogCardV2 blog={blog} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      ) : null}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.APP_ENV === 'development') {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const [{ data: collections = [] }] = await ApiClient.getCollections({
    per_page: 200,
    q: {
      redirect_url_blank: 1,
      s: ['featured desc', 'options_count desc']
    },
  });

  return {
    paths: flatten(
      collections.map((collection) => {
        return [{ params: { id: collection.slugCode } }];
      })
    ),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<CollectionPage> = async (context) => {
  const { id } = context.params || {};

  if (!id) {
    return {
      notFound: true,
    };
  }

  const [data] = await ApiClient.getCollectionPage({ id: String(id) });

  if ('error' in data) {
    return {
      notFound: true,
    };
  }

  if (data.collection.redirectUrl) {
    return {
      redirect: {
        destination: data.collection.redirectUrl,
        statusCode: 301
      }
    }
  }

  return {
    props: data,
    revalidate: RevalidateSettings.short,
  };
};

export default CollectionShow;
